import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question } from '../../types';
import { X, Wallet, RotateCcw } from 'lucide-react';
import { error as logError } from '../../utils/logger';
interface MoneyAssemblerProps {
  question: Question;
  onComplete: (userAnswer: string) => void;
  onReadyChange?: (isReady: boolean) => void;
  shouldValidate?: boolean;
}
export function MoneyAssembler({
  question,
  onComplete,
  onReadyChange,
  shouldValidate = false
}: MoneyAssemblerProps) {
  // CANONICAL: Read ONLY from data.money (no legacy fallback)
  const moneyConfig = question.data?.money as any;
  if (!moneyConfig || !moneyConfig.allowedDenoms) {
    logError('MoneyAssembler: Missing money config in question.data.money');
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-4260">
        <p className="text-red-700 font-bold" data-id="element-4261">⚠️ Geld configuratie ontbreekt</p>
        <p className="text-sm text-red-600 mt-2" data-id="element-4262">
          Deze vraag heeft geen geldige geld data.
        </p>
      </div>;
  }
  const allowedDenoms = moneyConfig.allowedDenoms || [];
  const availableDenominations = allowedDenoms.map((d: any) => d.valueCents);
  const [selectedCoins, setSelectedCoins] = useState<number[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const isReady = selectedCoins.length > 0;
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);
  useEffect(() => {
    if (shouldValidate) {
      // CANONICAL PAYLOAD: JSON with counts per denomination
      const countMap = new Map<number, number>();
      selectedCoins.forEach(cents => {
        countMap.set(cents, (countMap.get(cents) || 0) + 1);
      });
      // Build canonical token set (only count > 0)
      const tokens = Array.from(countMap.entries()).filter(([_, count]) => count > 0).map(([valueCents, count]) => ({
        valueCents,
        count
      }));
      // Serialize as JSON
      const userAnswer = JSON.stringify(tokens);
      onComplete(userAnswer);
    }
  }, [shouldValidate, selectedCoins, onComplete]);
  // Reset when question changes
  useEffect(() => {
    setSelectedCoins([]);
    setFocusedIndex(0);
  }, [question.id]);
  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Arrow keys to navigate
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setFocusedIndex(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setFocusedIndex(prev => Math.min(availableDenominations.length - 1, prev + 1));
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleAddCoin(availableDenominations[focusedIndex]);
      } else if (e.key === 'Backspace' && selectedCoins.length > 0) {
        e.preventDefault();
        handleRemoveCoin(selectedCoins.length - 1);
      } else if (e.key === 'Escape' && selectedCoins.length > 0) {
        e.preventDefault();
        handleReset();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [availableDenominations, focusedIndex, selectedCoins]);
  const handleAddCoin = (denomination: number) => {
    setSelectedCoins([...selectedCoins, denomination]);
  };
  const handleRemoveCoin = (index: number) => {
    setSelectedCoins(selectedCoins.filter((_, i) => i !== index));
  };
  const handleReset = () => {
    setSelectedCoins([]);
  };
  const getDenominationDisplay = (cents: number): string => {
    // Try to use label from config first
    const denom = allowedDenoms.find((d: any) => d.valueCents === cents);
    if (denom?.label) {
      return denom.label;
    }
    // Fallback to € formatting
    if (cents >= 100) return `€${cents / 100}`;
    return `${cents}`;
  };
  const isBill = (cents: number) => {
    const denom = allowedDenoms.find((d: any) => d.valueCents === cents);
    return denom?.type === 'bill';
  };
  const getMoneyStyles = (cents: number) => {
    // BILLS - Enhanced with better visual hierarchy
    if (cents === 5000) {
      return {
        container: 'w-28 h-16 md:w-32 md:h-18 rounded-xl shadow-xl',
        bg: 'bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 border-orange-700',
        text: 'text-white font-black text-2xl md:text-3xl drop-shadow-lg',
        border: 'border-4 border-opacity-40',
        inner: 'bg-white/15 backdrop-blur-sm'
      };
    }
    if (cents === 2000) {
      return {
        container: 'w-26 h-15 md:w-30 md:h-17 rounded-xl shadow-xl',
        bg: 'bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 border-blue-700',
        text: 'text-white font-black text-2xl md:text-3xl drop-shadow-lg',
        border: 'border-4 border-opacity-40',
        inner: 'bg-white/15 backdrop-blur-sm'
      };
    }
    if (cents === 1000) {
      return {
        container: 'w-24 h-14 md:w-28 md:h-16 rounded-xl shadow-xl',
        bg: 'bg-gradient-to-br from-rose-400 via-rose-500 to-rose-600 border-rose-700',
        text: 'text-white font-black text-xl md:text-2xl drop-shadow-lg',
        border: 'border-4 border-opacity-40',
        inner: 'bg-white/15 backdrop-blur-sm'
      };
    }
    if (cents === 500) {
      return {
        container: 'w-22 h-13 md:w-26 md:h-15 rounded-xl shadow-lg',
        bg: 'bg-gradient-to-br from-slate-400 via-slate-500 to-slate-600 border-slate-700',
        text: 'text-white font-black text-xl md:text-2xl drop-shadow-lg',
        border: 'border-3 border-opacity-40',
        inner: 'bg-white/15 backdrop-blur-sm'
      };
    }
    // LARGE COINS - Enhanced with better metallic effects
    if (cents === 200) {
      return {
        container: 'w-16 h-16 md:w-18 md:h-18 rounded-full shadow-2xl',
        bg: 'bg-gradient-to-br from-slate-300 via-slate-100 to-slate-400 border-slate-500',
        text: 'text-slate-800 font-black text-lg md:text-xl',
        border: 'border-[3px]',
        inner: 'bg-gradient-to-br from-amber-300 via-yellow-100 to-amber-500 rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center shadow-inner border-2 border-amber-600/40'
      };
    }
    if (cents === 100) {
      return {
        container: 'w-14 h-14 md:w-16 md:h-16 rounded-full shadow-2xl',
        bg: 'bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-500 border-amber-600',
        text: 'text-amber-900 font-black text-lg md:text-xl',
        border: 'border-[3px]',
        inner: 'bg-gradient-to-br from-slate-300 via-slate-100 to-slate-400 rounded-full w-9 h-9 md:w-11 md:h-11 flex items-center justify-center shadow-inner border-2 border-slate-500/40'
      };
    }
    if (cents === 50) {
      return {
        container: 'w-14 h-14 md:w-16 md:h-16 rounded-full shadow-xl',
        bg: 'bg-gradient-to-br from-yellow-300 via-yellow-100 to-yellow-500 border-yellow-600',
        text: 'text-yellow-900 font-black text-base md:text-lg',
        border: 'border-[3px]',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    // SMALL COINS - IMPROVED: Better sizing, gradients, and text rendering
    if (cents === 20) {
      return {
        container: 'w-12 h-12 md:w-14 md:h-14 rounded-full shadow-lg',
        bg: 'bg-gradient-to-br from-yellow-400 via-yellow-200 to-yellow-500 border-yellow-600',
        text: 'text-yellow-900 font-black text-base md:text-lg',
        border: 'border-[3px]',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    if (cents === 10) {
      return {
        container: 'w-11 h-11 md:w-13 md:h-13 rounded-full shadow-lg',
        bg: 'bg-gradient-to-br from-yellow-400 via-yellow-200 to-yellow-500 border-yellow-700',
        text: 'text-yellow-900 font-black text-sm md:text-base',
        border: 'border-[3px]',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    if (cents === 5) {
      return {
        container: 'w-10 h-10 md:w-12 md:h-12 rounded-full shadow-md',
        bg: 'bg-gradient-to-br from-orange-400 via-orange-300 to-orange-500 border-orange-700',
        text: 'text-orange-900 font-black text-sm md:text-base',
        border: 'border-[3px]',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    if (cents === 2) {
      return {
        container: 'w-9 h-9 md:w-11 md:h-11 rounded-full shadow-md',
        bg: 'bg-gradient-to-br from-orange-400 via-orange-300 to-orange-500 border-orange-700',
        text: 'text-orange-900 font-black text-xs md:text-sm',
        border: 'border-[2.5px]',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    if (cents === 1) {
      return {
        container: 'w-8 h-8 md:w-10 md:h-10 rounded-full shadow-md',
        bg: 'bg-gradient-to-br from-orange-400 via-orange-300 to-orange-500 border-orange-700',
        text: 'text-orange-900 font-black text-xs md:text-sm',
        border: 'border-[2.5px]',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    return {
      container: 'w-12 h-12 rounded-full',
      bg: 'bg-gray-200',
      text: 'text-gray-800',
      border: 'border',
      inner: ''
    };
  };
  const MoneyItem = ({
    cents,
    onClick,
    disabled = false,
    small = false,
    isFocused = false
  }: {
    cents: number;
    onClick?: () => void;
    disabled?: boolean;
    small?: boolean;
    isFocused?: boolean;
  }) => {
    const styles = getMoneyStyles(cents);
    const isBillItem = isBill(cents);
    const scaleClass = small ? 'scale-75 origin-center' : '';
    return <motion.button whileHover={!disabled && !small ? {
      scale: 1.08,
      rotate: isBillItem ? 0 : 2,
      y: -3
    } : {}} whileTap={!disabled && !small ? {
      scale: 0.92
    } : {}} onClick={onClick} disabled={disabled} className={`
          relative flex items-center justify-center
          ${styles.container} ${styles.bg} ${styles.border}
          ${scaleClass}
          transition-all duration-200
          ${!disabled && !small ? 'hover:shadow-2xl cursor-pointer' : ''}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${isFocused ? 'ring-4 ring-indigo-400 ring-offset-2' : ''}
        `} aria-label={getDenominationDisplay(cents)} data-id="element-4263">
        <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/30 to-white/0 rounded-[inherit] pointer-events-none" data-id="element-4264" />
        <div className={`${styles.inner} relative z-10`} data-id="element-4265">
          <span className={`${styles.text} drop-shadow-md`} data-id="element-4266">
            {getDenominationDisplay(cents)}
            {cents < 100 && <span className="text-[0.65em] ml-0.5" data-id="element-4267">c</span>}
          </span>
        </div>
        {isBillItem && <div className="absolute right-3 top-0 bottom-0 w-2 bg-white/15 border-l border-r border-white/25 rounded-r-lg" data-id="element-4268" />}
      </motion.button>;
  };
  return <div className="w-full max-w-3xl mx-auto space-y-6" data-id="element-4269">
      {/* Keyboard hints - only on desktop */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center hidden md:block" data-id="element-4270">
        <p className="text-xs text-indigo-700 font-medium" data-id="element-4271">
          💡 <strong data-id="element-4272">Toetsenbord:</strong> ← → om te navigeren • Enter/Spatie om
          te selecteren • Backspace om laatste te verwijderen • Esc om te
          resetten
        </p>
      </div>

      {/* Step 1: Selection */}
      <div className="space-y-3" data-id="element-4273">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-4274">
          <span className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px]" data-id="element-4275">
            1
          </span>
          Kies munten en biljetten
        </h3>

        <div className="bg-white rounded-2xl p-4 md:p-6 border border-slate-200 shadow-sm" data-id="element-4276">
          <div className="flex flex-wrap gap-3 md:gap-4 justify-center items-center" data-id="element-4277">
            {availableDenominations.map((denomination: number, index: number) => <motion.div key={`denom-${denomination}`} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.03
          }} data-id="element-4278">
                  <MoneyItem cents={denomination} onClick={() => handleAddCoin(denomination)} isFocused={index === focusedIndex} data-id="element-4279" />
                </motion.div>)}
          </div>
        </div>
      </div>

      {/* Step 2: Wallet */}
      <div className="space-y-3" data-id="element-4280">
        <div className="flex items-center justify-between" data-id="element-4281">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-4282">
            <span className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px]" data-id="element-4283">
              2
            </span>
            Jouw portemonnee
          </h3>

          {/* Reset Button */}
          {selectedCoins.length > 0 && <motion.button initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} onClick={handleReset} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors" data-id="element-4284">
              <RotateCcw className="w-3.5 h-3.5" data-id="element-4285" />
              Opnieuw
            </motion.button>}
        </div>

        <div className="relative min-h-[140px] bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300 overflow-hidden" data-id="element-4286">
          {selectedCoins.length === 0 && <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 pointer-events-none" data-id="element-4287">
              <Wallet className="w-10 h-10 mb-2 opacity-30" data-id="element-4288" />
              <p className="text-sm font-medium" data-id="element-4289">De portemonnee is nog leeg</p>
            </div>}

          <div className="relative z-10 p-4 flex flex-wrap content-start gap-2" data-id="element-4290">
            <AnimatePresence mode="popLayout" data-id="element-4291">
              {selectedCoins.map((coin, index) => <motion.div key={`selected-${index}-${coin}`} layout initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} exit={{
              scale: 0,
              opacity: 0
            }} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} className="relative group/coin" data-id="element-4292">
                  <MoneyItem cents={coin} small data-id="element-4293" />
                  <button onClick={() => handleRemoveCoin(index)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md opacity-100 md:opacity-0 md:group-hover/coin:opacity-100 transition-opacity" aria-label="Verwijder" data-id="element-4294">
                    <X className="w-3 h-3" data-id="element-4295" />
                  </button>
                </motion.div>)}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}